<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import type { ArticleCardResponse } from '@/api';
import { formatArticleDate, getArticleRoute, keepAmountsTogether } from '@/utilities/articles.ts';

const {
  articles,
  lead = false,
  compact = false,
} = defineProps<{
  articles: ArticleCardResponse[];
  lead?: boolean;
  compact?: boolean;
}>();

const { locale } = useI18n();
</script>

<template>
  <ul class="divide-y divide-gray-100" role="list">
    <li v-for="(article, index) in articles" :key="article.id" class="py-4 first:pt-0">
      <RouterLink
        :class="[lead && index === 0 ? 'sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] sm:gap-6' : compact ? 'grid-cols-[5.5rem_minmax(0,1fr)]' : 'grid-cols-[6.5rem_minmax(0,1fr)] sm:grid-cols-[10.5rem_minmax(0,1fr)]']"
        :to="getArticleRoute(article)"
        class="group grid grid-cols-1 items-start gap-4"
      >
        <div :class="[lead && index === 0 ? 'aspect-[16/10]' : 'aspect-[4/3]', article.image ? '' : 'from-ph-green-100 to-ph-green-300 bg-gradient-to-br']" class="w-full overflow-hidden rounded-md bg-gray-100">
          <img
            v-if="article.image"
            :alt="article.imageAltText || article.title"
            :loading="index === 0 ? 'eager' : 'lazy'"
            :src="lead && index === 0 ? article.image.resizedToMedium() : article.image.resizedToSmall()"
            class="h-full w-full object-cover"
          />
        </div>
        <div class="min-w-0">
          <p class="flex flex-wrap items-center gap-x-2 text-xs">
            <span v-if="article.category" class="text-ph-green-700 font-semibold">{{ article.category.name }}</span>
            <span v-if="article.category" aria-hidden="true" class="text-gray-300">·</span>
            <time :datetime="article.publishedOn" class="text-gray-500 tabular-nums">{{ formatArticleDate(article.publishedOn, locale) }}</time>
          </p>
          <h3 :class="lead && index === 0 ? 'text-lg md:text-xl' : compact ? 'text-sm' : 'text-base'" class="group-hover:text-ph-green-700 mt-0.5 font-semibold text-balance text-gray-900">
            {{ keepAmountsTogether(article.title) }}
          </h3>
          <p v-if="!compact" :class="lead && index === 0 ? 'line-clamp-3' : 'hidden sm:line-clamp-2'" class="mt-1 text-sm text-gray-600">{{ keepAmountsTogether(article.excerpt) }}</p>
        </div>
      </RouterLink>
    </li>
  </ul>
</template>
